import DOMPurify from 'dompurify';

/**
 * Tags no edited body may carry: active content, forms, and anything that
 * would load or navigate on its own. `<style>` stays — inside the editor's
 * sandboxed frame it styles the mail and nothing else.
 */
const FORBID_TAGS = [
  'applet',
  'base',
  'button',
  'embed',
  'form',
  'frame',
  'frameset',
  'iframe',
  'input',
  'link',
  'meta',
  'noscript',
  'object',
  'script',
  'select',
  'template',
  'textarea',
];
const FORBID_ATTR = ['action', 'formaction', 'ping', 'srcset'];

/** The editor's own marks in the live document; never part of the stored HTML. */
export const BASE_STYLE_ATTR = 'data-nx-html-editor';
export const SELECTED_IMAGE_ATTR = 'data-nx-html-editor-selected';

const DOCUMENT_PATTERN = /<(?:!doctype|html|head|body)[\s>]/i;
const DOCTYPE_PATTERN = /^\s*<!doctype[^>]*>/i;

/** A whole document (`<html>`, `<head>`, `<body>`) rather than a fragment. */
export function isHtmlDocument(html: string): boolean {
  return DOCUMENT_PATTERN.test(html);
}

/**
 * Removes active content. Keeps what a mail needs to look like itself: inline
 * and `<style>` CSS, tables, `data-*` markers (the BDMS quote) and comments
 * (the Mail signature markers), `cid:` and `data:` images.
 */
/** Outlook's conditional comments: layout only Outlook sees (`<!--[if mso]>…<![endif]-->`). */
const CONDITIONAL = /<!--\[if ([^\]<>]{1,120})\]>([\s\S]*?)<!\[endif\]-->/gi;
const UNSAFE_IN_CONDITIONAL =
  /<\s*\/?\s*(?:script|iframe|frame|object|embed|applet|form|base|link|meta)\b|\son[a-z]+\s*=|javascript:|vbscript:|<!--|-->/i;
const CONDITIONAL_SLOT = /<!--nx-conditional-(\d+)-->/g;

/**
 * DOMPurify drops a comment with markup in it, and with it the table an
 * Outlook layout keeps in `<!--[if mso]>`. Browsers never parse a comment, so
 * such a block is set aside and put back unchanged — unless it holds anything
 * active, then it goes.
 */
function keepConditionals(html: string, clean: (protectedHtml: string) => string): string {
  const kept: string[] = [];
  const protectedHtml = html.replace(
    CONDITIONAL,
    (whole: string, _condition: string, inner: string) => {
      if (UNSAFE_IN_CONDITIONAL.test(inner)) return '';
      kept.push(whole);
      return `<!--nx-conditional-${kept.length - 1}-->`;
    }
  );
  if (!kept.length) return clean(html);
  return clean(protectedHtml).replace(
    CONDITIONAL_SLOT,
    (_slot, index: string) => kept[Number(index)] ?? ''
  );
}

export function sanitizeHtml(html: string, wholeDocument = isHtmlDocument(html)): string {
  return keepConditionals(html, (input) => sanitizeOnly(input, wholeDocument));
}

function sanitizeOnly(html: string, wholeDocument: boolean): string {
  const options = {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['#comment', 'style'],
    ADD_ATTR: ['target'],
    FORBID_TAGS,
    FORBID_ATTR,
  };
  if (!wholeDocument) {
    // A fragment is body content. Parsed bare, a <style> it starts with would
    // land in <head> and be lost; an explicit <body> keeps it where it was.
    const clean = DOMPurify.sanitize(
      `<!doctype html><html><head></head><body>${html}</body></html>`,
      options
    );
    return new DOMParser().parseFromString(clean, 'text/html').body.innerHTML;
  }
  const clean = DOMPurify.sanitize(html, options);
  const doctype = DOCTYPE_PATTERN.exec(html)?.[0];
  return doctype ? `${doctype.trim()}\n${clean}` : clean;
}

/** Base look of an empty field — Write's: its sans stack, 11 pt, 1.4 lines. */
export const BASE_STYLE = `
html { background: #fff; overscroll-behavior: contain; }
body {
  margin: 0;
  padding: 12px 16px;
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 11pt;
  line-height: 1.4;
  color: #1f2937;
  overflow-wrap: break-word;
  outline: none;
}
/* Inside a field the text sits on the field, as every other input's does:
   no page of its own, the field's font and colour (set by the host). */
html:has(> body[data-nx-html-editor-flow]) {
  background: transparent;
  overscroll-behavior-y: auto;
}
body[data-nx-html-editor-flow] {
  padding: 0;
  font-family: var(--nx-html-editor-field-font, inherit);
  font-size: var(--nx-html-editor-field-size, 11pt);
  line-height: var(--nx-html-editor-field-leading, 1.4);
  color: var(--nx-html-editor-field-ink, #1f2937);
  /* Block formatting context: a last-child margin (inline or nested) stays
     inside the body, so the frame height counts it. */
  display: flow-root;
}
body[data-nx-html-editor-flow] > :first-child { margin-top: 0; }
body[data-nx-html-editor-flow] > :last-child { margin-bottom: 0; }
/* The frame is as tall as its text and never scrolls itself (the field does), so
   it must not stop the vertical wheel (overscroll-behavior-y, above) — and a mail
   wider than the field wraps to it instead of getting a bar inside the frame.
   Zero specificity (:where), so a mail's own style sheet still wins. */
:where(body[data-nx-html-editor-flow]) table { max-width: 100%; }
:where(body[data-nx-html-editor-flow]) pre { white-space: pre-wrap; }
body[data-nx-html-editor-empty]::before {
  content: attr(data-nx-html-editor-empty);
  color: #9ca3af;
  pointer-events: none;
  position: absolute;
}
img { max-width: 100%; height: auto; }
img[${SELECTED_IMAGE_ATTR}] { outline: 2px solid #bc209c; outline-offset: 1px; }
::highlight(nx-html-editor-find) { background: #fde68a; }
`;

/**
 * Parses stored HTML into a document for the editing frame, sanitized. A
 * fragment lands in an otherwise empty `<body>`; a whole document keeps its
 * `<head>`, so its `<style>` keeps applying.
 */
export function parseForEditing(html: string): Document {
  const wholeDocument = isHtmlDocument(html);
  const clean = sanitizeHtml(html, wholeDocument);
  const doc = new DOMParser().parseFromString(
    wholeDocument ? clean : `<!doctype html><html><head></head><body>${clean}</body></html>`,
    'text/html'
  );
  const style = doc.createElement('style');
  style.setAttribute(BASE_STYLE_ATTR, '');
  style.textContent = BASE_STYLE;
  doc.head.prepend(style);
  return doc;
}

/**
 * The HTML to store for the live document: the editor's own marks removed,
 * a fragment as the `<body>` content, a whole document as itself.
 * `bodyAttributes` are the attributes the stored `<body>` had; whatever the
 * editor put on it besides (editable, ARIA role, label) is not stored.
 */
export function serializeForStorage(
  doc: Document,
  wholeDocument: boolean,
  doctype?: string,
  bodyAttributes: ReadonlySet<string> = new Set()
): string {
  const root = doc.documentElement.cloneNode(true) as HTMLElement;
  root.querySelectorAll(`style[${BASE_STYLE_ATTR}]`).forEach((el) => el.remove());
  root
    .querySelectorAll(`[${SELECTED_IMAGE_ATTR}]`)
    .forEach((el) => el.removeAttribute(SELECTED_IMAGE_ATTR));
  const body = root.querySelector('body');
  if (body) {
    for (const name of [...body.getAttributeNames()]) {
      if (!bodyAttributes.has(name)) body.removeAttribute(name);
    }
  }
  if (!wholeDocument) return sanitizeHtml(body?.innerHTML ?? '', false);
  const html = root.outerHTML;
  return sanitizeHtml(doctype ? `${doctype}\n${html}` : html, true);
}

/** The doctype a stored document starts with, kept on save. */
export function doctypeOf(html: string): string | undefined {
  return DOCTYPE_PATTERN.exec(html)?.[0].trim();
}

/** Plain text of an HTML body, for word counts and empty checks. */
export function textOf(body: HTMLElement | null | undefined): string {
  return body?.innerText ?? body?.textContent ?? '';
}

const OFFICE_PATTERN = /urn:schemas-microsoft-com:office|class="?Mso|mso-[a-z-]+\s*:/i;

/** HTML copied from Word or Outlook. */
export function isOfficeHtml(html: string): boolean {
  return OFFICE_PATTERN.test(html);
}

const KEEP_ATTRS: Record<string, readonly string[]> = {
  a: ['href'],
  img: ['src', 'alt', 'width', 'height'],
  td: ['colspan', 'rowspan'],
  th: ['colspan', 'rowspan'],
  ol: ['start'],
};

/**
 * Pasted HTML that takes the look of the place it lands in: structure
 * (paragraphs, lists, tables, links, emphasis) stays, the source's fonts,
 * sizes, colours and classes go.
 */
export function matchDestination(html: string): string {
  const doc = new DOMParser().parseFromString(sanitizeHtml(html, false), 'text/html');
  doc.querySelectorAll('style').forEach((el) => el.remove());
  doc.querySelectorAll('font, span, o\\:p').forEach((el) => el.replaceWith(...el.childNodes));
  doc.body.querySelectorAll('*').forEach((el) => {
    const keep = KEEP_ATTRS[el.localName] ?? [];
    for (const name of [...el.getAttributeNames()])
      if (!keep.includes(name)) el.removeAttribute(name);
  });
  return doc.body.innerHTML;
}

/** Escaped plain text as paragraphs, line breaks kept. */
export function textToHtml(text: string): string {
  const escape = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escape(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}
